import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

interface Message {
  role: "user" | "assistant";
  content: string;
}

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content: "Hello! I'm here to help with your home renovation needs. How can I assist you today?",
    },
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const chatMutation = useMutation<{ reply: string }, Error, string>({
    mutationFn: async (message: string) => {
      const conversationHistory = messages.slice(1);
      return apiRequest<{ reply: string }>("POST", "/api/chat", {
        message,
        conversationHistory,
      });
    },
    onSuccess: (data: { reply: string }) => {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.reply },
      ]);
    },
    onError: () => {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "I apologize, but I'm having trouble responding. Please call us at (832) 998-9595 for immediate assistance.",
        },
      ]);
    },
  });

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || chatMutation.isPending) return;

    const userMessage = inputMessage.trim();
    setInputMessage("");

    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);

    chatMutation.mutate(userMessage);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages]);

  return (
    <>
      {!isOpen && (
        <button
          className="fixed bottom-8 right-8 flex items-center gap-3 px-6 py-4 rounded-full bg-gradient-to-r from-[#00d9ff] via-[#00b8e6] to-[#0099cc] border-4 border-white shadow-[0_0_40px_rgba(0,217,255,0.8),0_0_80px_rgba(0,217,255,0.5),0_0_120px_rgba(0,217,255,0.3)] hover:shadow-[0_0_60px_rgba(0,217,255,1),0_0_100px_rgba(0,217,255,0.7)] hover:scale-110 transition-all duration-300 z-[9997] animate-pulse-glow cursor-pointer"
          onClick={() => setIsOpen(true)}
          data-testid="button-chat-open"
          aria-label="Open chat assistant"
        >
          <MessageCircle className="h-8 w-8 text-white drop-shadow-lg" />
          <span className="text-white font-bold text-lg drop-shadow-lg">Chat with us!</span>
        </button>
      )}

      {isOpen && (
        <div
          className="fixed bottom-8 right-8 w-[380px] h-[600px] glass-card border-2 border-[#00d9ff]/50 rounded-2xl shadow-[0_0_40px_rgba(0,217,255,0.4)] flex flex-col overflow-hidden z-[9997]"
          data-testid="container-chat-widget"
        >
          <div className="glass-header p-4 border-b border-[#00d9ff]/20 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00d9ff] to-[#ffd700] flex items-center justify-center">
                <MessageCircle className="h-6 w-6 text-[#0a0e27]" />
              </div>
              <div>
                <h3 className="font-heading font-bold gradient-text text-lg">
                  HHS Build Assistant
                </h3>
                <p className="text-xs text-muted-foreground">
                  Usually responds within seconds
                </p>
              </div>
            </div>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={() => setIsOpen(false)}
              data-testid="button-chat-close"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <ScrollArea ref={scrollAreaRef} className="flex-1 p-4">
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  data-testid={`message-${msg.role}-${idx}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-2xl ${
                      msg.role === "user"
                        ? "bg-gradient-to-br from-[#00d9ff]/20 to-[#00d9ff]/10 border border-[#00d9ff]/30 text-foreground"
                        : "glass-surface border border-[#ffd700]/20 text-foreground"
                    }`}
                  >
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">
                      {msg.content}
                    </p>
                  </div>
                </div>
              ))}
              {chatMutation.isPending && (
                <div className="flex justify-start" data-testid="message-loading">
                  <div className="glass-surface border border-[#ffd700]/20 p-3 rounded-2xl">
                    <Loader2 className="h-5 w-5 animate-spin text-[#00d9ff]" />
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          <div className="p-4 border-t border-[#00d9ff]/20 glass-header">
            <div className="flex gap-2">
              <Input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type a message..."
                className="flex-1 glass-surface border-[#00d9ff]/20"
                disabled={chatMutation.isPending}
                data-testid="input-chat-message"
              />
              <Button
                size="icon"
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || chatMutation.isPending}
                className="h-10 w-10 bg-gradient-to-br from-[#00d9ff] to-[#00d9ff]/80 hover:glow-cyan"
                data-testid="button-chat-send"
              >
                {chatMutation.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Send className="h-5 w-5" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Or call us: (832) 998-9595
            </p>
          </div>
        </div>
      )}
    </>
  );
}
